import React, { useState, useEffect } from 'react';
import Swal from 'sweetalert2';
import webOrderService from '../../../services/webOrderService';
import '../../../pages/Pages.css';
import './WebOrders.css';

const WebOrders = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [showMapModal, setShowMapModal] = useState(false);
  
  // Pagination and filters
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalOrders, setTotalOrders] = useState(0);
  const [perPage] = useState(20);
  
  // Filters
  const [filters, setFilters] = useState({
    status: '',
    payment_method: '',
    search: '',
    per_page: perPage
  });

  // Edit form data
  const [editFormData, setEditFormData] = useState({
    additional_notes: '',
    payment_method: '',
    customer_phone: '',
    customer_address: ''
  });

  // Status form data
  const [statusFormData, setStatusFormData] = useState({
    status: ''
  });

  // Assign form data
  const [assignFormData, setAssignFormData] = useState({
    assigned_driver_id: '',
    assigned_cashier_id: ''
  });

  useEffect(() => {
    loadOrders();
  }, [currentPage, filters]);

  const loadOrders = async () => {
    try {
      setLoading(true);
      const response = await webOrderService.getAllWebOrders({
        ...filters,
        page: currentPage,
        per_page: perPage
      });
      
      setOrders(response.data.data);
      setTotalPages(response.data.last_page || 1);
      setTotalOrders(response.data.total || 0);
    } catch (error) {
      Swal.fire('Error!', error.message || 'Failed to load web orders', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Check if order is home delivery
  const isHomeDelivery = (order) => {
    return order.latitude && order.longitude;
  };

  // Get delivery type badge
  const getDeliveryTypeBadge = (order) => {
    return isHomeDelivery(order) ? '🏠 Home Delivery' : '🏪 Store Pickup';
  };

  // Open Google Maps for delivery address
  const openGoogleMaps = (order) => {
    if (isHomeDelivery(order)) {
      const { latitude, longitude } = order;
      const mapsUrl = `https://www.google.com/maps?q=${latitude},${longitude}`;
      window.open(mapsUrl, '_blank');
    }
  };

  // Show map in modal
  const showMapModalForOrder = (order) => {
    if (isHomeDelivery(order)) {
      setSelectedOrder(order);
      setShowMapModal(true);
    }
  };

  // Generate Google Maps embed URL
  const getGoogleMapsEmbedUrl = (order) => {
    if (!order.latitude || !order.longitude) return null;
    return `https://maps.google.com/maps?q=${order.latitude},${order.longitude}&z=15&output=embed`;
  };

  const handleViewOrder = async (order) => {
    try {
      const response = await webOrderService.getWebOrderById(order.id);
      setSelectedOrder(response.data);
      setShowViewModal(true);
    } catch (error) {
      Swal.fire('Error!', error.message || 'Failed to load order details', 'error');
    }
  };

  const handleEditOrder = async (order) => {
    try {
      const response = await webOrderService.getWebOrderById(order.id);
      setSelectedOrder(response.data);
      setEditFormData({
        additional_notes: response.data.additional_notes || '',
        payment_method: response.data.payment_method,
        customer_phone: response.data.customer_phone || '',
        customer_address: response.data.customer_address || ''
      });
      setShowEditModal(true);
    } catch (error) {
      Swal.fire('Error!', error.message || 'Failed to load order details', 'error');
    }
  };

  const handleStatusChange = (order) => {
    setSelectedOrder(order);
    setStatusFormData({
      status: order.status
    });
    setShowStatusModal(true);
  };

  const handleAssignStaff = (order) => {
    setSelectedOrder(order);
    setAssignFormData({
      assigned_driver_id: order.assigned_driver_id || '',
      assigned_cashier_id: order.assigned_cashier_id || ''
    });
    setShowAssignModal(true);
  };

  const handleUpdateOrder = async (e) => {
    e.preventDefault();
    try {
      await webOrderService.updateWebOrder(selectedOrder.id, editFormData);
      Swal.fire('Success!', 'Web order updated successfully', 'success');
      setShowEditModal(false);
      loadOrders();
    } catch (error) {
      Swal.fire('Error!', error.message || 'Failed to update web order', 'error');
    }
  };

  const handleUpdateStatus = async (e) => {
    e.preventDefault();
    try {
      await webOrderService.updateWebOrderStatus(selectedOrder.id, statusFormData.status);
      Swal.fire('Success!', 'Web order status updated successfully', 'success');
      setShowStatusModal(false);
      loadOrders();
    } catch (error) {
      Swal.fire('Error!', error.message || 'Failed to update web order status', 'error');
    }
  };

  const handleAssignStaffSubmit = async (e) => {
    e.preventDefault();
    try {
      if (assignFormData.assigned_driver_id) {
        await webOrderService.assignDriver(selectedOrder.id, assignFormData.assigned_driver_id);
      }
      if (assignFormData.assigned_cashier_id) {
        await webOrderService.assignCashier(selectedOrder.id, assignFormData.assigned_cashier_id);
      }
      Swal.fire('Success!', 'Staff assigned successfully', 'success');
      setShowAssignModal(false);
      loadOrders();
    } catch (error) {
      Swal.fire('Error!', error.message || 'Failed to assign staff', 'error');
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setCurrentPage(1);
  };

  const clearFilters = () => {
    setFilters({
      status: '',
      payment_method: '',
      search: '',
      per_page: perPage
    });
    setCurrentPage(1);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'LKR'
    }).format(amount || 0);
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'confirmed': return 'confirmed';
      case 'pending': return 'pending';
      case 'processing': return 'processing';
      case 'ready_for_delivery': return 'ready';
      case 'out_for_delivery': return 'out-for-delivery';
      case 'delivered': return 'delivered';
      case 'cancelled': return 'cancelled';
      default: return 'pending';
    }
  };

  const getPaymentMethodLabel = (method) => {
    switch (method) {
      case 'cash': return '💵 Cash';
      case 'card': return '💳 Card';
      case 'digital_wallet': return '📱 Digital Wallet';
      case 'bank_transfer': return '🏦 Bank Transfer';
      default: return method;
    }
  };

  const getCustomerFullName = (order) => {
    return `${order.customer_first_name} ${order.customer_last_name}`.trim();
  };

  // Pagination controls
  const renderPagination = () => {
    const pages = [];
    const maxVisiblePages = 5;
    
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
    
    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <button
          key={i}
          className={`pagination-btn ${currentPage === i ? 'active' : ''}`}
          onClick={() => setCurrentPage(i)}
        >
          {i}
        </button>
      );
    }

    return (
      <div className="pagination">
        <button
          className="pagination-btn"
          onClick={() => setCurrentPage(1)}
          disabled={currentPage === 1}
        >
          ««
        </button>
        <button
          className="pagination-btn"
          onClick={() => setCurrentPage(currentPage - 1)}
          disabled={currentPage === 1}
        >
          «
        </button>
        
        {startPage > 1 && (
          <>
            <button
              className="pagination-btn"
              onClick={() => setCurrentPage(1)}
            >
              1
            </button>
            {startPage > 2 && <span className="pagination-ellipsis">...</span>}
          </>
        )}
        
        {pages}
        
        {endPage < totalPages && (
          <>
            {endPage < totalPages - 1 && <span className="pagination-ellipsis">...</span>}
            <button
              className="pagination-btn"
              onClick={() => setCurrentPage(totalPages)}
            >
              {totalPages}
            </button>
          </>
        )}
        
        <button
          className="pagination-btn"
          onClick={() => setCurrentPage(currentPage + 1)}
          disabled={currentPage === totalPages}
        >
          »
        </button>
        <button
          className="pagination-btn"
          onClick={() => setCurrentPage(totalPages)}
          disabled={currentPage === totalPages}
        >
          »»
        </button>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="page-container">
        <div className="page-header">
          <h1>Web Orders Management</h1>
          <p>Manage and track e-commerce orders</p>
        </div>
        <div className="loading">Loading web orders...</div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="page-header">
        <div className="header-content">
          <div>
            <h1>Web Orders Management</h1>
            <p>
              Manage and track e-commerce orders 
              {totalOrders > 0 && ` (${totalOrders} total orders, showing ${orders.length} on this page)`}
            </p>
          </div>
          <button className="btn btn-secondary" onClick={loadOrders}>
            🔄 Refresh
          </button>
        </div>
      </div>

      {/* Filters Section */}
      <div className="filters-section single-row">
        <div className="filters-row">
          <div className="filter-group">
            <input
              type="text"
              placeholder="Search by order number or customer name..."
              value={filters.search}
              onChange={(e) => handleFilterChange('search', e.target.value)}
              className="filter-input"
            />
          </div>

          <div className="filter-group">
            <select
              value={filters.status}
              onChange={(e) => handleFilterChange('status', e.target.value)}
              className="filter-select"
            >
              <option value="">All Status</option>
              <option value="pending">Pending</option>
              <option value="confirmed">Confirmed</option>
              <option value="processing">Processing</option>
              <option value="ready_for_delivery">Ready for Delivery</option>
              <option value="out_for_delivery">Out for Delivery</option>
              <option value="ready_for_pickup">Ready For Pickup</option>
              <option value="picked_up">Picked Up</option>
              <option value="delivered">Delivered</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>

          <div className="filter-group">
            <select
              value={filters.payment_method}
              onChange={(e) => handleFilterChange('payment_method', e.target.value)}
              className="filter-select"
            >
              <option value="">All Payment Methods</option>
              <option value="cash">Cash</option>
              <option value="bank_transfer">Bank Transfer</option>
            </select>
          </div>

          <div className="filter-actions">
            <button className="btn btn-clear" onClick={clearFilters} title="Clear Filters">
              🗑️ Clear
            </button>
          </div>
        </div>
      </div>

      <div className="page-content">
        <div className="orders-table-container">
          <table className="orders-table">
            <thead>
              <tr>
                <th>Order #</th>
                <th>Delivery Type</th>
                <th>Date</th>
                <th>Customer</th>
                <th>Contact</th>
                <th>Items</th>
                <th>Total Amount</th>
                <th>Delivery Fee</th>
                <th>Payment Method</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {orders.map(order => (
                <tr key={order.id}>
                  <td className="order-number">
                    <strong>{order.order_number}</strong>
                  </td>
                  <td>
                    <span className={`delivery-type-badge ${isHomeDelivery(order) ? 'home-delivery' : 'store-pickup'}`}>
                      {getDeliveryTypeBadge(order)}
                    </span>
                    {isHomeDelivery(order) && (
                      <div className="map-actions-small">
                        <button 
                          className="btn btn-map-small"
                          onClick={() => showMapModalForOrder(order)}
                          title="View Delivery Location"
                        >
                          🗺️
                        </button>
                      </div>
                    )}
                  </td>
                  <td>{formatDate(order.created_at)}</td>
                  <td>
                    <div className="customer-info">
                      <strong>{getCustomerFullName(order)}</strong>
                      {order.customer_email && (
                        <div className="customer-email">{order.customer_email}</div>
                      )}
                    </div>
                  </td>
                  <td>
                    {order.customer_phone && (
                      <div className="contact-info">
                        <span className="phone">{order.customer_phone}</span>
                        {order.customer_address && (
                          <span className="address" title={order.customer_address}>
                            {order.customer_address.substring(0, 30)}...
                          </span>
                        )}
                      </div>
                    )}
                  </td>
                  <td>
                    <div className="items-count">
                      {order.items.length} item{order.items.length !== 1 ? 's' : ''}
                    </div>
                    <div className="items-preview">
                      {order.items.slice(0, 2).map(item => (
                        <span key={item.id} className="item-name">
                          {item.product_name}
                          {item.variant_name && ` (${item.variant_name})`}
                        </span>
                      ))}
                      {order.items.length > 2 && (
                        <span className="more-items">+{order.items.length - 2} more</span>
                      )}
                    </div>
                  </td>
                  <td className="amount-cell">
                    <strong>{formatCurrency(order.total_amount)}</strong>
                    <div className="breakdown">
                      <small>Sub: {formatCurrency(order.subtotal)}</small>
                      <small>Tax: {formatCurrency(order.tax_amount)}</small>
                    </div>
                  </td>
                  <td>
                    {formatCurrency(order.delivery_fee_amount)}
                  </td>
                  <td>
                    <span className="payment-method">
                      {getPaymentMethodLabel(order.payment_method)}
                    </span>
                    {order.payment_verified && (
                      <div className="payment-verified">✅ Verified</div>
                    )}
                  </td>
                  <td>
                    <span className={`status-badge ${getStatusBadgeClass(order.status)}`}>
                      {order.status.replace(/_/g, ' ')}
                    </span>
                    {order.assigned_driver_id && (
                      <div className="assigned-info">🚗 Driver Assigned</div>
                    )}
                    {order.assigned_cashier_id && (
                      <div className="assigned-info">👨‍💼 Cashier Assigned</div>
                    )}
                  </td>
                  <td>
                    <div className="action-buttons">
                      <button 
                        className="btn btn-view"
                        onClick={() => handleViewOrder(order)}
                        title="View Order Details"
                      >
                        👁️
                      </button>
                      <button 
                        className="btn btn-edit"
                        onClick={() => handleEditOrder(order)}
                        title="Edit Order"
                      >
                        ✏️
                      </button>
                      <button 
                        className="btn btn-status"
                        onClick={() => handleStatusChange(order)}
                        title="Change Status"
                      >
                        🔄
                      </button>
                      {/* <button 
                        className="btn btn-assign"
                        onClick={() => handleAssignStaff(order)}
                        title="Assign Staff"
                      >
                        👥
                      </button> */}
                      {isHomeDelivery(order) && (
                        <button 
                          className="btn btn-map"
                          onClick={() => openGoogleMaps(order)}
                          title="Open in Google Maps"
                        >
                          🗺️
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          
          {orders.length === 0 && (
            <div className="no-data">
              <div className="no-data-icon">🛒</div>
              <h3>No Web Orders Found</h3>
              <p>
                {filters.status || filters.payment_method || filters.search 
                  ? 'No web orders match your current filters.' 
                  : 'No e-commerce orders have been placed yet.'
                }
              </p>
              {filters.status || filters.payment_method || filters.search ? (
                <button className="btn btn-primary" onClick={clearFilters}>
                  Clear Filters
                </button>
              ) : null}
            </div>
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && renderPagination()}
      </div>

      {/* View Order Modal */}
      {showViewModal && selectedOrder && (
        <ViewWebOrderModal
          order={selectedOrder}
          onClose={() => setShowViewModal(false)}
          formatDate={formatDate}
          formatCurrency={formatCurrency}
          getStatusBadgeClass={getStatusBadgeClass}
          getPaymentMethodLabel={getPaymentMethodLabel}
          getCustomerFullName={getCustomerFullName}
          isHomeDelivery={isHomeDelivery}
          getDeliveryTypeBadge={getDeliveryTypeBadge}
          openGoogleMaps={openGoogleMaps}
          showMapModalForOrder={showMapModalForOrder}
        />
      )}

      {/* Map Modal */}
      {showMapModal && selectedOrder && (
        <MapModal
          order={selectedOrder}
          onClose={() => setShowMapModal(false)}
          getGoogleMapsEmbedUrl={getGoogleMapsEmbedUrl}
          openGoogleMaps={openGoogleMaps}
          formatCurrency={formatCurrency}
        />
      )}

      {/* Edit Order Modal */}
      {showEditModal && selectedOrder && (
        <EditWebOrderModal
          order={selectedOrder}
          formData={editFormData}
          setFormData={setEditFormData}
          onClose={() => setShowEditModal(false)}
          onSubmit={handleUpdateOrder}
          formatCurrency={formatCurrency}
        />
      )}

      {/* Status Change Modal */}
      {showStatusModal && selectedOrder && (
        <WebOrderStatusChangeModal
          order={selectedOrder}
          formData={statusFormData}
          setFormData={setStatusFormData}
          onClose={() => setShowStatusModal(false)}
          onSubmit={handleUpdateStatus}
          getStatusBadgeClass={getStatusBadgeClass}
        />
      )}

      {/* Assign Staff Modal */}
      {showAssignModal && selectedOrder && (
        <AssignStaffModal
          order={selectedOrder}
          formData={assignFormData}
          setFormData={setAssignFormData}
          onClose={() => setShowAssignModal(false)}
          onSubmit={handleAssignStaffSubmit}
        />
      )}
    </div>
  );
};

// View Web Order Modal Component
const ViewWebOrderModal = ({ 
  order, 
  onClose, 
  formatDate, 
  formatCurrency, 
  getStatusBadgeClass, 
  getPaymentMethodLabel, 
  getCustomerFullName,
  isHomeDelivery,
  getDeliveryTypeBadge,
  openGoogleMaps,
  showMapModalForOrder
}) => {
  return (
    <div className="modal-overlay">
      <div className="modal modal-large">
        <div className="modal-header">
          <h3>Web Order Details - {order.order_number}</h3>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <div className="modal-body">
          <div className="order-detail-view">
            {/* Order Header */}
            <div className="order-header">
              <div className="order-meta">
                <div className="meta-item">
                  <label>Order Number:</label>
                  <span>{order.order_number}</span>
                </div>
                <div className="meta-item">
                  <label>Delivery Type:</label>
                  <span className={`delivery-type-badge ${isHomeDelivery(order) ? 'home-delivery' : 'store-pickup'}`}>
                    {getDeliveryTypeBadge(order)}
                  </span>
                </div>
                <div className="meta-item">
                  <label>Order Date:</label>
                  <span>{formatDate(order.created_at)}</span>
                </div>
                <div className="meta-item">
                  <label>Status:</label>
                  <span className={`status-badge ${getStatusBadgeClass(order.status)}`}>
                    {order.status.replace(/_/g, ' ')}
                  </span>
                </div>
                <div className="meta-item">
                  <label>Payment Method:</label>
                  <span>{getPaymentMethodLabel(order.payment_method)}</span>
                  {order.payment_verified && <span className="verified-badge">✅ Verified</span>}
                </div>
              </div>
            </div>

            {/* Customer Information */}
            <div className="customer-section">
              <h4>Customer Information</h4>
              <div className="customer-details">
                <div className="detail-row">
                  <label>Name:</label>
                  <span>{getCustomerFullName(order)}</span>
                </div>
                <div className="detail-row">
                  <label>Email:</label>
                  <span>{order.customer_email || 'N/A'}</span>
                </div>
                <div className="detail-row">
                  <label>Phone:</label>
                  <span>{order.customer_phone || 'N/A'}</span>
                </div>
                {order.customer_address && (
                  <div className="detail-row">
                    <label>Address:</label>
                    <div className="address-with-map">
                      <span>{order.customer_address}</span>
                      {isHomeDelivery(order) && (
                        <div className="map-actions">
                          <button 
                            className="btn btn-map"
                            onClick={() => openGoogleMaps(order)}
                          >
                            🗺️ Open Maps
                          </button>
                          <button 
                            className="btn btn-map"
                            onClick={() => showMapModalForOrder(order)}
                          >
                            📍 View Map
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                )}
                {(order.customer_city || order.customer_postal_code) && (
                  <div className="detail-row">
                    <label>Location:</label>
                    <span>
                      {[order.customer_city, order.customer_postal_code].filter(Boolean).join(', ')}
                    </span>
                  </div>
                )}
                {order.delivery_distance > 0 && (
                  <div className="detail-row">
                    <label>Delivery Distance:</label>
                    <span>{order.delivery_distance} km</span>
                  </div>
                )}
                {order.delivery_fee_amount > 0 && (
                  <div className="detail-row">
                    <label>Delivery Fee:</label>
                    <span>{formatCurrency(order.delivery_fee_amount)}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Order Summary */}
            <div className="order-summary">
              <h4>Order Summary</h4>
              <div className="summary-grid">
                <div className="summary-item">
                  <label>Subtotal:</label>
                  <span>{formatCurrency(order.subtotal)}</span>
                </div>
                <div className="summary-item">
                  <label>Tax:</label>
                  <span>{formatCurrency(order.tax_amount)}</span>
                </div>
                {order.delivery_fee_amount > 0 && (
                  <div className="summary-item">
                    <label>Delivery Fee:</label>
                    <span>{formatCurrency(order.delivery_fee_amount)}</span>
                  </div>
                )}
                <div className="summary-item total">
                  <label>Total Amount:</label>
                  <span>{formatCurrency(order.total_amount)}</span>
                </div>
              </div>
            </div>

            {/* Order Items */}
            <div className="order-items">
              <h4>Order Items ({order.items.length})</h4>
              <div className="items-table">
                <table>
                  <thead>
                    <tr>
                      <th>Product</th>
                      <th>Variant</th>
                      <th>Quantity</th>
                      <th>Unit Price</th>
                      <th>Total Price</th>
                    </tr>
                  </thead>
                  <tbody>
                    {order.items.map(item => (
                      <tr key={item.id}>
                        <td>
                          <div className="product-info">
                            <strong>{item.product_name}</strong>
                          </div>
                        </td>
                        <td>{item.variant_name || 'N/A'}</td>
                        <td>{item.quantity}</td>
                        <td>{formatCurrency(item.unit_price)}</td>
                        <td>{formatCurrency(item.total_price)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Additional Notes */}
            {order.additional_notes && (
              <div className="order-notes">
                <h4>Additional Notes</h4>
                <div className="notes-content">
                  {order.additional_notes}
                </div>
              </div>
            )}

            {/* Payment Information */}
            {order.transaction_id && (
              <div className="payment-details">
                <h4>Payment Details</h4>
                <div className="payment-info">
                  <div className="detail-row">
                    <label>Transaction ID:</label>
                    <span>{order.transaction_id}</span>
                  </div>
                  {order.bank_name && (
                    <div className="detail-row">
                      <label>Bank Name:</label>
                      <span>{order.bank_name}</span>
                    </div>
                  )}
                  {order.bank_receipt_url && (
                    <div className="detail-row">
                      <label>Bank Receipt:</label>
                      <a href={order.bank_receipt_url} target="_blank" rel="noopener noreferrer">
                        View Receipt
                      </a>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

// Map Modal Component
const MapModal = ({ order, onClose, getGoogleMapsEmbedUrl, openGoogleMaps, formatCurrency }) => {
  return (
    <div className="modal-overlay">
      <div className="modal modal-large">
        <div className="modal-header">
          <h3>Delivery Location - Order #{order.order_number}</h3>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <div className="modal-body">
          <div className="customer-info-map">
            <div className="customer-details-map">
              <h4>Customer Information</h4>
              <div className="detail-grid">
                <div className="detail-item">
                  <label>Name:</label>
                  <span>{order.customer_first_name} {order.customer_last_name}</span>
                </div>
                <div className="detail-item">
                  <label>Phone:</label>
                  <span>{order.customer_phone}</span>
                </div>
                <div className="detail-item">
                  <label>Address:</label>
                  <span>{order.customer_address}</span>
                </div>
                {order.delivery_distance > 0 && (
                  <div className="detail-item">
                    <label>Distance:</label>
                    <span>{order.delivery_distance} km</span>
                  </div>
                )}
                {order.delivery_fee_amount > 0 && (
                  <div className="detail-item">
                    <label>Delivery Fee:</label>
                    <span>{formatCurrency(order.delivery_fee_amount)}</span>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="map-container-large">
            {getGoogleMapsEmbedUrl(order) ? (
              <iframe
                src={getGoogleMapsEmbedUrl(order)}
                width="100%"
                height="400"
                style={{ border: 0, borderRadius: '8px' }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Delivery Location Map"
              />
            ) : (
              <div className="no-map-available">
                <p>Map not available for this location</p>
              </div>
            )}
          </div>
        </div>
        <div className="modal-footer">
          <button 
            className="btn btn-secondary"
            onClick={() => openGoogleMaps(order)}
          >
            🗺️ Open in Google Maps
          </button>
          <button className="btn btn-primary" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

// Edit Web Order Modal Component
const EditWebOrderModal = ({ order, formData, setFormData, onClose, onSubmit, formatCurrency }) => {
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  return (
    <div className="modal-overlay">
      <div className="modal">
        <div className="modal-header">
          <h3>Edit Web Order - {order.order_number}</h3>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <form onSubmit={onSubmit}>
          <div className="modal-body">
            <div className="edit-order-form">
              <div className="form-group">
                <label>Payment Method</label>
                <select
                  name="payment_method"
                  value={formData.payment_method}
                  onChange={handleInputChange}
                >
                  <option value="cash">Cash</option>
                  <option value="bank_transfer">Bank Transfer</option>
                </select>
              </div>

              <div className="form-group">
                <label>Customer Phone</label>
                <input
                  type="text"
                  name="customer_phone"
                  value={formData.customer_phone}
                  onChange={handleInputChange}
                  placeholder="Enter customer phone number"
                />
              </div>

              <div className="form-group full-width">
                <label>Customer Address</label>
                <textarea
                  name="customer_address"
                  value={formData.customer_address}
                  onChange={handleInputChange}
                  rows="3"
                  placeholder="Enter customer delivery address..."
                />
              </div>

              <div className="form-group full-width">
                <label>Additional Notes</label>
                <textarea
                  name="additional_notes"
                  value={formData.additional_notes}
                  onChange={handleInputChange}
                  rows="3"
                  placeholder="Enter any additional notes..."
                />
              </div>

              <div className="current-info">
                <h5>Current Order Information</h5>
                <div className="info-grid">
                  <div className="info-item">
                    <label>Total Amount:</label>
                    <span>{formatCurrency(order.total_amount)}</span>
                  </div>
                  <div className="info-item">
                    <label>Current Payment Method:</label>
                    <span>{order.payment_method}</span>
                  </div>
                  <div className="info-item">
                    <label>Customer:</label>
                    <span>{order.customer_first_name} {order.customer_last_name}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary">
              Update Order
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Web Order Status Change Modal Component
const WebOrderStatusChangeModal = ({ order, formData, setFormData, onClose, onSubmit, getStatusBadgeClass }) => {
  const handleStatusChange = (e) => {
    setFormData({ status: e.target.value });
  };

  const statusOptions = [
    { value: 'pending', label: 'Pending', description: 'Order has been placed but not confirmed' },
    { value: 'confirmed', label: 'Confirmed', description: 'Order has been confirmed and is awaiting processing' },
    { value: 'processing', label: 'Processing', description: 'Order is being prepared and packaged' },
    { value: 'ready_for_delivery', label: 'Ready for Delivery', description: 'Order is ready for pickup by delivery driver' },
    { value: 'ready_for_pickup', label: 'Ready for Pickup', description: 'Order is ready for customer pickup' },
    { value: 'picked_up', label: 'Picked Up', description: 'Order has been picked up by customer' },
    { value: 'out_for_delivery', label: 'Out for Delivery', description: 'Order is with delivery driver and on the way to customer' },
    { value: 'delivered', label: 'Delivered', description: 'Order has been successfully delivered to customer' },
    { value: 'cancelled', label: 'Cancelled', description: 'Order has been cancelled' }
  ];

  return (
    <div className="modal-overlay">
      <div className="modal">
        <div className="modal-header">
          <h3>Change Web Order Status - {order.order_number}</h3>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <form onSubmit={onSubmit}>
          <div className="modal-body">
            <div className="status-change-form">
              <div className="current-status">
                <label>Current Status:</label>
                <span className={`status-badge ${getStatusBadgeClass(order.status)}`}>
                  {order.status.replace(/_/g, ' ')}
                </span>
              </div>

              <div className="form-group">
                <label>New Status *</label>
                <select
                  value={formData.status}
                  onChange={handleStatusChange}
                  required
                >
                  <option value="">Select new status</option>
                  {statusOptions.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              {formData.status && (
                <div className="status-description">
                  <strong>
                    {statusOptions.find(opt => opt.value === formData.status)?.label}
                  </strong>
                  <p>{statusOptions.find(opt => opt.value === formData.status)?.description}</p>
                </div>
              )}

              <div className="status-warning">
                <p>⚠️ Changing order status will update the order timeline and may trigger notifications to the customer.</p>
              </div>
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary">
              Update Status
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Assign Staff Modal Component
const AssignStaffModal = ({ order, formData, setFormData, onClose, onSubmit }) => {
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // Mock data - replace with actual API calls to get drivers and cashiers
  const drivers = [
    { id: 1, name: 'John Driver', phone: '+94710000001' },
    { id: 2, name: 'Jane Smith', phone: '+94710000002' }
  ];

  const cashiers = [
    { id: 1, name: 'Alice Johnson', employee_id: 'EMP001' },
    { id: 2, name: 'Bob Wilson', employee_id: 'EMP002' }
  ];

  return (
    <div className="modal-overlay">
      <div className="modal">
        <div className="modal-header">
          <h3>Assign Staff - {order.order_number}</h3>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <form onSubmit={onSubmit}>
          <div className="modal-body">
            <div className="assign-staff-form">
              <div className="form-group">
                <label>Assign Driver</label>
                <select
                  name="assigned_driver_id"
                  value={formData.assigned_driver_id}
                  onChange={handleInputChange}
                >
                  <option value="">Select Driver</option>
                  {drivers.map(driver => (
                    <option key={driver.id} value={driver.id}>
                      {driver.name} ({driver.phone})
                    </option>
                  ))}
                </select>
                <small>Assign a driver for order delivery</small>
              </div>

              <div className="form-group">
                <label>Assign Cashier</label>
                <select
                  name="assigned_cashier_id"
                  value={formData.assigned_cashier_id}
                  onChange={handleInputChange}
                >
                  <option value="">Select Cashier</option>
                  {cashiers.map(cashier => (
                    <option key={cashier.id} value={cashier.id}>
                      {cashier.name} ({cashier.employee_id})
                    </option>
                  ))}
                </select>
                <small>Assign a cashier for order processing</small>
              </div>

              <div className="current-assignments">
                <h5>Current Assignments</h5>
                <div className="assignment-info">
                  <div className="assignment-item">
                    <label>Driver:</label>
                    <span>
                      {order.assigned_driver_id 
                        ? `Driver #${order.assigned_driver_id}` 
                        : 'Not assigned'
                      }
                    </span>
                  </div>
                  <div className="assignment-item">
                    <label>Cashier:</label>
                    <span>
                      {order.assigned_cashier_id 
                        ? `Cashier #${order.assigned_cashier_id}` 
                        : 'Not assigned'
                      }
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary">
              Assign Staff
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default WebOrders;